import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useI18n } from '@/i18n';
import { variboxCapacityOptions, variboxContractOptions, variboxSealOptions, type VariboxStatusKey } from '@/data/variboxMockData';
import { getLocationTree } from '@/services/locationApi';
import { checkVariboxDuplicates, createVaribox, type VariboxDuplicateFields } from '@/services/variboxApi';

type ClientOption = {
  id: string;
  name: string;
};

type FormState = {
  serialNumber: string;
  radexIdentifier: string;
  barcode: string;
  capacityL: string;
  color: string;
  sealType: string;
  status: VariboxStatusKey;
  contract: 'tak' | 'nie';
  rfidTag: string;
  locationId: string;
  clientId: string;
  notes: string;
};

const initialState: FormState = {
  serialNumber: '',
  radexIdentifier: '',
  barcode: '',
  capacityL: '250',
  color: 'niebieski',
  sealType: 'VITON',
  status: 'available',
  contract: 'tak',
  rfidTag: '',
  locationId: '',
  clientId: '',
  notes: '',
};

const statusOptions: VariboxStatusKey[] = ['available', 'occupied', 'damaged', 'repair', 'cleaning', 'blocked'];
const noDuplicates: VariboxDuplicateFields = { serial_number: false, radex_identifier: false, barcode: false };

export default function VariboxCreateDialog({
  open,
  clientOptions,
  onClose,
  onCreated,
}: {
  open: boolean;
  clientOptions: ClientOption[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<FormState>(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [duplicateFields, setDuplicateFields] = useState(noDuplicates);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateCheckError, setDuplicateCheckError] = useState(false);
  const [locationOptions, setLocationOptions] = useState<Array<{ id: string; label: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    void getLocationTree().then(({ items }) => {
      const options: Array<{ id: string; label: string }> = [];
      const walk = (nodes: Awaited<ReturnType<typeof getLocationTree>>['items'], depth: number, rootCode: string | null) => {
        for (const node of nodes) {
          const nextRootCode = depth === 0 ? node.code : rootCode;
          if (depth > 0 && node.kind !== 'root' && (nextRootCode === 'PL01' || nextRootCode === 'PL99')) {
            const prefix = `${'  '.repeat(depth - 1)}- `;
            options.push({ id: node.id, label: `${prefix}${node.code ? `${node.code} ` : ''}${node.name}` });
          }
          walk(node.children, depth + 1, nextRootCode);
        }
      };
      walk(items, 0, null);
      if (!cancelled) setLocationOptions(options);
    }).catch(() => {
      if (!cancelled) setLocationOptions([]);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (open) {
      setForm(initialState);
      setErrorMessage('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const serialNumber = form.serialNumber.trim();
    const radexIdentifier = form.radexIdentifier.trim();
    const barcode = form.barcode.trim();
    setDuplicateFields(noDuplicates);
    setCheckingDuplicates(false);
    setDuplicateCheckError(false);
    if (!serialNumber && !radexIdentifier && !barcode) return;
    setCheckingDuplicates(true);

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void checkVariboxDuplicates({ serial_number: serialNumber, radex_identifier: radexIdentifier, barcode })
        .then((duplicates) => {
          if (!cancelled) setDuplicateFields(duplicates);
        })
        .catch(() => {
          if (!cancelled) setDuplicateCheckError(true);
        })
        .finally(() => {
          if (!cancelled) setCheckingDuplicates(false);
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.barcode, form.radexIdentifier, form.serialNumber, open]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage('');

    try {
      await createVaribox({
        serial_number: form.serialNumber.trim(),
        radex_identifier: form.radexIdentifier.trim(),
        barcode: form.barcode.trim(),
        capacity_l: Number(form.capacityL),
        color: form.color.trim(),
        seal_type: form.sealType,
        status: form.status,
        contract: form.contract === 'tak',
        rfid_tag: form.rfidTag.trim() || null,
        current_location_id: form.locationId,
        current_client_id: form.clientId || null,
        notes: form.notes.trim() || null,
      });
      onCreated();
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.trim()) {
          if (detail === 'Duplicate serial_number') {
            setDuplicateFields((current) => ({ ...current, serial_number: true }));
            setErrorMessage(t('assets.createDialog.duplicateSerial'));
          } else if (detail === 'Duplicate radex_identifier') {
            setDuplicateFields((current) => ({ ...current, radex_identifier: true }));
            setErrorMessage(t('assets.createDialog.duplicateRadex'));
          } else if (detail === 'Duplicate barcode') {
            setDuplicateFields((current) => ({ ...current, barcode: true }));
            setErrorMessage(t('assets.createDialog.duplicateBarcode'));
          } else {
            setErrorMessage(detail);
          }
        } else {
          setErrorMessage(t('assets.createDialog.error'));
        }
      } else {
        setErrorMessage(t('assets.createDialog.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(
    form.serialNumber.trim() &&
      form.radexIdentifier.trim() &&
      form.barcode.trim() &&
      form.capacityL.trim() &&
      form.color.trim() &&
      form.sealType.trim() &&
      form.locationId &&
      form.status &&
      form.contract,
  ) && !duplicateFields.serial_number && !duplicateFields.radex_identifier && !duplicateFields.barcode;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('assets.createDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('assets.createDialog.description')}
          </Typography>
                <Typography variant="caption" color="text.secondary">
                  Numer seryjny, RADEX i kod kreskowy muszą być unikalne.
                </Typography>

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {duplicateCheckError ? <Alert severity="warning">{t('assets.createDialog.duplicateCheckError')}</Alert> : null}
          {checkingDuplicates ? <Typography variant="caption" color="text.secondary">{t('assets.createDialog.checkingDuplicates')}</Typography> : null}
          {duplicateFields.serial_number || duplicateFields.radex_identifier || duplicateFields.barcode ? (
            <Alert severity="error">{t('assets.createDialog.duplicateWarning')}</Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label={t('assets.createDialog.serialNumber')} value={form.serialNumber} onChange={(event) => update('serialNumber', event.target.value)} error={duplicateFields.serial_number} helperText={duplicateFields.serial_number ? t('assets.createDialog.duplicateSerial') : undefined} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('assets.createDialog.radexIdentifier')} value={form.radexIdentifier} onChange={(event) => update('radexIdentifier', event.target.value)} error={duplicateFields.radex_identifier} helperText={duplicateFields.radex_identifier ? t('assets.createDialog.duplicateRadex') : undefined} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('assets.createDialog.barcode')} value={form.barcode} onChange={(event) => update('barcode', event.target.value)} error={duplicateFields.barcode} helperText={duplicateFields.barcode ? t('assets.createDialog.duplicateBarcode') : undefined} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.capacity')} value={form.capacityL} onChange={(event) => update('capacityL', event.target.value)} fullWidth>
                {variboxCapacityOptions.map((capacity) => (
                  <MenuItem key={capacity} value={String(capacity)}>{capacity} L</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('assets.createDialog.color')} value={form.color} onChange={(event) => update('color', event.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.sealType')} value={form.sealType} onChange={(event) => update('sealType', event.target.value)} fullWidth>
                {variboxSealOptions.map((sealType) => (
                  <MenuItem key={sealType} value={sealType}>{sealType}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.status')} value={form.status} onChange={(event) => update('status', event.target.value as VariboxStatusKey)} fullWidth>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>{t(`assets.statuses.${status}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.contract')} value={form.contract} onChange={(event) => update('contract', event.target.value as 'tak' | 'nie')} fullWidth>
                {variboxContractOptions.map((contract) => (
                  <MenuItem key={contract} value={contract}>{t(`assets.contractValues.${contract}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('assets.createDialog.rfidTag')} value={form.rfidTag} onChange={(event) => update('rfidTag', event.target.value)} helperText={t('assets.createDialog.rfidTagHelp')} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select required label="Podmagazyn (PL01 / PL99)" value={form.locationId} onChange={(event) => update('locationId', event.target.value)} helperText={locationOptions.length ? 'Wybierz podmagazyn; magazyny główne nie są dostępne do przypisania.' : 'Najpierw utwórz podmagazyn pod PL01 lub PL99.'} fullWidth>
                <MenuItem value="">Wybierz podmagazyn</MenuItem>
                {locationOptions.map((location) => <MenuItem key={location.id} value={location.id}>{location.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.client')} value={form.clientId} onChange={(event) => update('clientId', event.target.value)} helperText={clientOptions.length > 0 ? t('assets.createDialog.clientPlaceholder') : t('assets.createDialog.noClients')} fullWidth>
                <MenuItem value="">{t('assets.createDialog.noClient')}</MenuItem>
                {clientOptions.map((client) => (
                  <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label={t('assets.createDialog.notes')} value={form.notes} onChange={(event) => update('notes', event.target.value)} multiline minRows={3} fullWidth />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>{t('common.buttons.close')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting || checkingDuplicates}>
          {submitting ? t('assets.createDialog.saving') : checkingDuplicates ? t('assets.createDialog.checkingDuplicates') : t('assets.createDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}